import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

// Extend dayjs with relativeTime plugin
dayjs.extend(relativeTime);

/**
 * Currency symbol mapping
 */
const CURRENCY_SYMBOLS = {
  GBP: '£',
  USD: '$',
  EUR: '€',
  JPY: '¥',
};

/**
 * Get currency symbol from currency code
 * @param {string} currency - Currency code or symbol
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (currency = 'GBP') => {
  return CURRENCY_SYMBOLS[currency] || currency;
};

/**
 * Format currency with symbol
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (e.g., 'GBP', 'USD') or symbol (default: 'GBP')
 * @param {number} decimals - Number of decimal places (default: auto-detect based on amount)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'GBP', decimals = null) => {
  const symbol = getCurrencySymbol(currency);
  const isNegative = amount < 0;
  const absAmount = Math.abs(amount);

  // Auto-detect appropriate decimal places if not specified
  // Show 2 decimals for amounts under 1000 or if there are significant cents
  // Show 0 decimals for large round amounts
  let finalDecimals = decimals;
  if (finalDecimals === null) {
    const hasCents = (absAmount % 1) !== 0;

    if (hasCents || absAmount < 1000) {
      finalDecimals = 2;
    } else {
      finalDecimals = 0;
    }
  }

  const formatted = absAmount.toLocaleString('en-GB', {
    minimumFractionDigits: finalDecimals,
    maximumFractionDigits: finalDecimals,
  });

  return isNegative ? `-${symbol}${formatted}` : `${symbol}${formatted}`;
};

/**
 * Format currency with sign
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (e.g., 'GBP', 'USD') or symbol (default: 'GBP')
 * @param {number} decimals - Number of decimal places (default: null for auto-detect)
 * @returns {string} Formatted currency string with +/- sign
 */
export const formatCurrencyWithSign = (amount, currency = 'GBP', decimals = null) => {
  const sign = amount >= 0 ? '+' : '-';
  return `${sign}${formatCurrency(Math.abs(amount), currency, decimals)}`;
};

/**
 * Format percentage
 * @param {number} percentage - Percentage to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (percentage, decimals = 2) => {
  return `${percentage.toFixed(decimals)}%`;
};

/**
 * Format percentage with sign
 * @param {number} percentage - Percentage to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted percentage string with +/- sign
 */
export const formatPercentageWithSign = (percentage, decimals = 2) => {
  // FIX: Don't show sign for zero
  if (percentage === 0) {
    return `${percentage.toFixed(decimals)}%`;
  }
  const sign = percentage >= 0 ? '+' : '';
  return `${sign}${percentage.toFixed(decimals)}%`;
};

/**
 * Format date
 * @param {string|Date} date - Date to format
 * @param {string} format - Format string (default: DD/MM/YYYY)
 * @returns {string} Formatted date string
 */
export const formatDate = (date, format = 'DD/MM/YYYY') => {
  return dayjs(date).format(format);
};

/**
 * Format date relative to now
 * @param {string|Date} date - Date to format
 * @returns {string} Relative date string (e.g., "2 days ago")
 */
export const formatRelativeDate = (date) => {
  // Validate date
  if (!date) return 'No date';

  const now = dayjs();
  const target = dayjs(date);

  // Check if date is valid
  if (!target.isValid()) return 'Invalid date';

  const diffDays = now.diff(target, 'day');
  const diffMonths = now.diff(target, 'month');
  const diffYears = now.diff(target, 'year');

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays === -1) return 'Tomorrow';
  if (diffDays > 1 && diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < -1 && diffDays > -7) return `In ${Math.abs(diffDays)} days`;
  if (diffMonths > 0 && diffMonths < 12) return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
  if (diffMonths < 0 && diffMonths > -12) return `In ${Math.abs(diffMonths)} month${Math.abs(diffMonths) > 1 ? 's' : ''}`;
  if (diffYears > 0) return `${diffYears} year${diffYears > 1 ? 's' : ''} ago`;
  if (diffYears < 0) return `In ${Math.abs(diffYears)} year${Math.abs(diffYears) > 1 ? 's' : ''}`;

  return formatDate(date);
};

/**
 * Format time ago using dayjs relative time
 * @param {string|Date} date - Date to format
 * @returns {string} Time ago string (e.g., "2 hours ago", "3 minutes ago")
 */
export const formatTimeAgo = (date) => {
  return dayjs(date).fromNow();
};

/**
 * Get color for positive/negative values
 * @param {number} value - Value to check
 * @returns {string} Color code
 */
export const getValueColor = (value) => {
  if (value > 0) return '#10b981'; // Green for positive
  if (value < 0) return '#ef4444'; // Red for negative
  return '#6b7280'; // Gray for zero
};

/**
 * Format transaction type for display
 * @param {string} type - Transaction type
 * @returns {string} Formatted type string
 */
export const formatTransactionType = (type) => {
  const types = {
    contribution: 'Contribution',
    withdrawal: 'Withdrawal',
    fee: 'Fee',
    dividend: 'Dividend',
    manual: 'Manual Adjustment',
  };
  return types[type] || type;
};

/**
 * Get status tag color
 * @param {string} status - Status string
 * @returns {string} Ant Design tag color
 */
export const getStatusColor = (status) => {
  const colors = {
    'on-track': 'success',
    'behind': 'warning',
    'achieved': 'success',
    'overdue': 'error',
  };
  return colors[status] || 'default';
};

/**
 * Format large numbers with K, M, B suffixes
 * @param {number} num - Number to format
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted number string
 */
export const formatCompactNumber = (num, decimals = 1) => {
  const absNum = Math.abs(num);
  if (absNum >= 1000000000) return (num / 1000000000).toFixed(decimals) + 'B';
  if (absNum >= 1000000) return (num / 1000000).toFixed(decimals) + 'M';
  if (absNum >= 1000) return (num / 1000).toFixed(decimals) + 'K';
  return num.toLocaleString('en-GB', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
};

/**
 * Format large currency values with K, M, B suffixes
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: 'GBP')
 * @param {number} decimals - Number of decimal places for compact notation (default: 1)
 * @returns {string} Formatted compact currency string (e.g., "£1.2M")
 */
export const formatCompactCurrency = (amount, currency = 'GBP', decimals = 1) => {
  const symbol = getCurrencySymbol(currency);
  const absAmount = Math.abs(amount);

  if (absAmount >= 1000000000) {
    return `${symbol}${(amount / 1000000000).toFixed(decimals)}B`;
  }
  if (absAmount >= 1000000) {
    return `${symbol}${(amount / 1000000).toFixed(decimals)}M`;
  }
  if (absAmount >= 1000) {
    return `${symbol}${(amount / 1000).toFixed(decimals)}K`;
  }

  // For smaller amounts, use standard currency formatting
  return formatCurrency(amount, currency, 2);
};

/**
 * Format units/shares with consistent decimal places
 * @param {number} units - Number of units/shares
 * @param {number} decimals - Number of decimal places (default: 4)
 * @returns {string} Formatted units string
 */
export const formatUnits = (units, decimals = 4) => {
  if (units === null || units === undefined || isNaN(units)) {
    return (0).toFixed(decimals);
  }
  return units.toLocaleString('en-GB', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Format exchange rate with consistent decimal places
 * @param {number} rate - Exchange rate value
 * @param {number} decimals - Number of decimal places (default: 4)
 * @returns {string} Formatted exchange rate string
 */
export const formatExchangeRate = (rate, decimals = 4) => {
  if (rate === null || rate === undefined || isNaN(rate)) {
    return (1).toFixed(decimals);
  }
  return rate.toFixed(decimals);
};
